import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Router } from '@angular/router';
import { FileInputError } from '@commons/file/file-input/file-input-error.enum';
import { EventTrackerService } from '@commons/tracker/event-tracker/event-tracker.service';
import { trackByIndex } from '@core/utils/utils';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { OpsEvent } from '@models/ops-event/ops-event';
import { Participation } from '@models/ops-event/participation';
import { ProfileParticipation } from '@models/ops-event/profile-participation';
import { VisitorParticipation } from '@models/ops-event/visitor-participation';
import { Profile } from '@models/profile/profile';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchRoutingEnum } from '@routing/search/search-routing.enum';
import { FileStoreService } from '@store/file/file-store.service';
import { CvStoreService } from '@store/profile/cv-store.service';
import { buildPath, WINDOW } from '@wizbii/angular-utilities';
import { WizFile } from '@wizbii/models';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'event-participation',
  templateUrl: './participation.component.html',
  styleUrls: ['./participation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipationComponent implements OnInit {
  @Input() model: OpsEvent;
  @Input() profile: Profile;
  @Input() loading = false;
  @Input() participated = false;
  @Input() displayHeader = true;
  @Input() returnToHome = false;
  @Output() participate = new EventEmitter<[string, Participation]>();
  @Output() closeForm = new EventEmitter<void>();

  trackByIndex = trackByIndex;

  isClosePresent: boolean;
  jobsPath = buildPath(FeaturesRoutingEnum.Search, SearchRoutingEnum.Jobs);
  cguUrl = `https://legal.${environment.apiDomain}/${environment.legalLang}/cpu/jobs`;
  policyUrl = `https://legal.${environment.apiDomain}/${environment.legalLang}/privacy-policy/jobs`;

  firstName: string;
  lastName: string;
  checkboxValue = false;

  files: WizFile[];
  cv: string;

  uploadPercent$: Observable<number>;

  error: FileInputError;

  FileInputError = FileInputError;

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly fileStoreService: FileStoreService,
    private readonly eventTrackerService: EventTrackerService,
    private readonly cvStoreService: CvStoreService,
    private readonly cookieService: CookieService,
    @Inject(WINDOW) private readonly window: any
  ) {
    this.uploadPercent$ = this.fileStoreService.uploadPercent$;
    this.files = [new WizFile()];
    this.cvStoreService.cvs$.pipe(untilDestroyed(this)).subscribe((next) => {
      this.files = [...Array.from(next.values()), new WizFile()];
      this.cdr.markForCheck();
    });
  }

  ngOnInit(): void {
    this.isClosePresent = this.closeForm.observers.length > 0;

    if (this.files && this.files.length) {
      this.cv = this.files[0].id;
    }
  }

  addNewSlot(wizFile: WizFile, control: NgControl): void {
    if (control.invalid) {
      this.error = control.errors['mimeTypes'] ? FileInputError.InvalidFormat : FileInputError.MaxSizeExceeded;
      return;
    }

    this.eventTrackerService.send({
      eventCategory: 'Search Events',
      eventAction: 'Click CV Upload',
    });
    this.files = [...this.files.slice(0, -1), wizFile];
    this.fileStoreService
      .create(wizFile)
      .pipe(take(1))
      .subscribe((next) => {
        if (this.profile) {
          this.cvStoreService.add(next);
        } else {
          this.files = [...this.files.slice(0, -1), next, new WizFile()];
        }
      });
  }

  doSubmit(values: object): void {
    if (this.model.type === 'evenement_prive' && !this.checkboxValue) {
      return;
    }

    const { searchParams } = new URL(`${this.window.location.origin}${this.router.url}`);

    const participationData = {
      ...values,
      utmSource: searchParams.get('utm_source') || undefined,
      utmMedium: searchParams.get('utm_medium') || undefined,
      utmCampaign: searchParams.get('utm_campaign') || undefined,
      utmTerm: searchParams.get('utm_term') || undefined,
      utmContent: searchParams.get('utm_content') || undefined,
    };

    if (!searchParams.get('utm_source')) {
      participationData.utmCampaign = this.model.id;
    }

    this.participate.emit([
      this.model.id,
      this.profile
        ? new ProfileParticipation({
            profileId: this.profile.id,
            firstName: this.profile.firstName,
            lastName: this.profile.lastName,
            ...participationData,
          })
        : new VisitorParticipation({
            visitorId: this.cookieService.get('uniqUserId'),
            ...participationData,
          }),
    ]);
  }
}
